import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Group } from '@mantine/core'
import { isString, omit } from 'lodash'
import { isEmpty, isEqual, noop, some } from 'lodash'
import React, { useEffect, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useMediaQuery } from 'react-responsive'
import { RouteComponentProps, withRouter } from 'react-router'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { TTheme } from '../../../ThemeContext'
import TransitionMantineTooltip from '../../../TransitionMantineTooltip'

type TWrapper = {
  active?: boolean
  theme?: TTheme
  disabled?: boolean
  isCompact?: boolean
}
const Wrapper = styled.div<TWrapper>`
  background: ${({ active }) => (active ? 'rgba(0, 0, 0, 0.06)' : '')};
  cursor: pointer;
  padding: ${({ isCompact }) =>
    isCompact ? '13px 14px 8px 14px' : '20px 20px 15px 20px'};
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  color: ${({ theme, disabled }) => (disabled ? '#36404a7d' : theme.linkColor)};
  text-decoration: none;

  &:hover {
    background: ${({ theme, disabled }) =>
      disabled ? 'none' : 'rgba(0, 0, 0, 0.03)'};
  }

  @media (max-width: 767px) {
    flex-direction: row;
    justify-content: flex-start;
    padding: 12px 25px 8px 25px;
  }
`

// const DropdownMenu = styled.div`
//   padding: 10px 0px;
//   display: ${({ show }) => (show ? '' : 'none')};
// `

const Icon = styled.i`
  font-size: 1.2em;
  margin-bottom: 5px;
  display: block;
  // opacity: 0.6;

  @media (max-width: 767px) {
    margin-right: 15px;
  }
`

const DropdownItem = styled.div`
  font-size: 1.1em;
  padding: 8px 15px;
`

type TChild = {
  label?: React.ReactNode
  icon?: string
  to?: string
  replaceTo?: string
  href?: string
  setIsHovered?: (val: boolean) => void
  onClick?: (props?: any) => void
}

const Child = (props: TChild): React.ReactElement => {
  const { label, icon, to, replaceTo, href, setIsHovered, onClick } = props

  const inner = (
    <Dropdown.Item as={DropdownItem}>
      <Group noWrap>
        {icon && isString(icon) && <Icon className={icon} />}
        {icon && !isString(icon) && <FontAwesomeIcon icon={icon} />}
        <span>{label}</span>
      </Group>
    </Dropdown.Item>
  )
  if (href || replaceTo) {
    return (
      <a style={{ cursor: 'pointer' }} href={replaceTo && to ? to : href}>
        {inner}
      </a>
    )
  }
  if (to) {
    return (
      <Link
        onClick={() => {
          if (setIsHovered) {
            setIsHovered(false)
          }

          if (onClick) {
            onClick(props)
          }
        }}
        to={to}
      >
        {inner}
      </Link>
    )
  }
  return inner
}

export type TItemNavbar = {
  onClick?: () => void
  show?: boolean
  to?: string
  href?: string
  disabled?: boolean
}

interface TItem extends RouteComponentProps {
  to?: string
  href?: string
  label?: React.ReactNode
  icon?: string
  theme?: TTheme
  children?: TItemNavbar[]
  currentPath?: string
  disabled?: boolean
  exact?: boolean
  onClickProp?: (props: any) => void
  replaceTo?: string
  onClick?: (props: any) => void
  isCompact?: boolean
}
const Item = (props: TItem): React.ReactElement => {
  const {
    to = '',
    href = '',
    label,
    icon,
    theme,
    children,
    replaceTo,
    onClick,
    disabled,
    exact,
    onClickProp,
    isCompact,
    ...rest
  } = props

  const [currentPathName, setCurrentPathName] = useState(
    window.location.pathname,
  )
  const [isHovered, setIsHovered] = useState(false)

  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })

  useEffect(() => {
    props.history.listen((location: any) => {
      setCurrentPathName(location.pathname)
    })
  }, [])

  //console.log("props", props)
  const onMouseEnter = (): void => {
    setIsHovered(true)
  }

  const onMouseLeave = (): void => {
    setIsHovered(false)
  }

  const getActive = (): boolean => {
    return children
      ? some(
          children,
          (child: TItem) =>
            child.href === currentPathName || child.to === currentPathName,
        )
      : (!exact && window.location.pathname.startsWith(href || to)) ||
          isEqual(window.location.pathname, href || to)
  }

  const innerClick = (): void => {
    if (!isEmpty(children) && isMobile) {
      setIsHovered(!isHovered)
    }
    onClick && onClick(props)
    if (onClickProp) {
      onClickProp(props)
    }
  }

  let inner = (
    <Wrapper
      theme={theme}
      onClick={innerClick}
      disabled={disabled}
      active={
        (href || to) !== '/' ? getActive() : window.location.pathname === '/'
      }
      isCompact={isCompact}
    >
      {icon && isString(icon) && <Icon className={icon} />}
      {icon && !isString(icon) && (
        <FontAwesomeIcon
          icon={icon}
          fontSize="1.2em"
          style={{ marginBottom: '5px' }}
        />
      )}
      <span>{label}</span>
    </Wrapper>
  )

  // if (size(children) === 1) {
  //   console.log(props, children)
  // }
  if (children) {
    inner = (
      <Dropdown
        onMouseEnter={disabled || isMobile ? noop : onMouseEnter}
        onMouseLeave={disabled || isMobile ? noop : onMouseLeave}
      >
        {inner}
        <div className={`dropdown-menu ${isHovered && 'show'} no-animation`}>
          {children
            .filter(Boolean)
            .filter((item) => item.show !== false)
            .map((child, index) => (
              <Child
                {...child}
                replaceTo={replaceTo}
                setIsHovered={setIsHovered}
                onClick={onClick}
                key={index}
              />
            ))}
        </div>
      </Dropdown>
    )
    // return <div>CHILDREN</div>
  }

  if (href || replaceTo) {
    const aProps = omit(rest, ['staticContext', 'show'])
    return (
      <a href={replaceTo ? href || to : href} {...aProps}>
        {disabled ? (
          <TransitionMantineTooltip
            tooltip={i18n.t('not_booked', 'Modul nicht gebucht')}
          >
            {inner}
          </TransitionMantineTooltip>
        ) : (
          inner
        )}
      </a>
    )
  }
  if (to) {
    // return <div>LINK</div>
    return <Link to={to}>{inner}</Link>
  }

  return disabled ? (
    <TransitionMantineTooltip
      tooltip={i18n.t('not_booked', 'Modul nicht gebucht')}
    >
      {inner}
    </TransitionMantineTooltip>
  ) : (
    inner
  )
}

export default withRouter(Item)
